import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, ArrowDownLeft, ArrowUpRight, Loader2, WalletCards } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, formatINR } from "../lib/api";

const QUICK_ADD = [100, 200, 500];

export default function Wallet() {
  const navigate = useNavigate();
  const [balance, setBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/account/wallet");
      setBalance(data.balance || 0);
      setTransactions(data.transactions || []);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const topupAmount = Math.max(0, Number(amount) || 0);

  const startVerifiedTopup = async () => {
    if (!window.Razorpay || !process.env.REACT_APP_RAZORPAY_KEY_ID) {
      toast.error("Razorpay checkout is unavailable. Please try again shortly.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/account/wallet/topup/create-order", {
        amount: topupAmount,
      });
      const checkout = new window.Razorpay({
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: data.amount,
        currency: data.currency,
        name: "Quick Door Now Wallet",
        description: "Wallet top-up",
        order_id: data.order_id,
        handler: async (response) => {
          try {
            const verified = await api.post("/account/wallet/topup/verify", response);
            setBalance(verified.data.balance);
            setAmount("");
            toast.success(`Verified top-up: ${formatINR(verified.data.amount)}`);
            load();
          } catch (error) {
            toast.error(formatApiError(error));
          } finally {
            setBusy(false);
          }
        },
        modal: { ondismiss: () => setBusy(false) },
      });
      checkout.on("payment.failed", (response) => {
        setBusy(false);
        toast.error(response.error?.description || "Wallet payment failed");
      });
      checkout.open();
    } catch (error) {
      toast.error(formatApiError(error));
      setBusy(false);
    }
  };

  return (
    <main className="mx-auto min-h-screen max-w-xl bg-[#F4F6F8] px-3 pb-28 pt-4" data-testid="wallet-screen">
      <header className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          data-testid="wallet-back"
          className="grid h-10 w-10 place-items-center rounded-full bg-white"
        >
          <ArrowLeft size={18} />
        </button>
        <h1 className="text-xl font-extrabold text-[#111111]">My Wallet</h1>
      </header>

      <section className="mt-4 rounded-2xl bg-gradient-to-br from-[#FF5200] to-[#ff2d55] p-5 text-white shadow-lg">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-white/80">
          <WalletCards size={16} /> Available balance
        </div>
        <p className="mt-2 text-4xl font-extrabold" data-testid="wallet-balance">
          {loading ? "…" : formatINR(balance)}
        </p>
      </section>

      <section className="mt-4 rounded-2xl bg-white p-4 shadow-sm" data-testid="wallet-topup">
        <h2 className="text-sm font-extrabold text-slate-900">Add money</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {QUICK_ADD.map((amt) => (
            <button
              key={amt}
              type="button"
              data-testid={`wallet-quick-${amt}`}
              onClick={() => setAmount(String((Number(amount) || 0) + amt))}
              className="min-h-10 rounded-full border border-orange-300 bg-orange-50 px-4 text-sm font-bold text-orange-700 transition-transform active:scale-95"
            >
              +₹{amt}
            </button>
          ))}
        </div>
        <input
          type="number"
          min="1"
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          placeholder="Enter amount"
          data-testid="wallet-amount-input"
          className="mt-3 min-h-12 w-full rounded-xl border border-slate-200 px-4 text-sm outline-none focus:border-[#FF5200]"
        />
        <button
          type="button"
          disabled={topupAmount <= 0 || busy}
          onClick={startVerifiedTopup}
          data-testid="wallet-add-money-button"
          className="mt-3 flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#FF5200] text-sm font-extrabold text-white transition-transform active:scale-95 disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Add Money to Wallet
        </button>
      </section>

      <section className="mt-4 rounded-2xl bg-white p-4 shadow-sm" data-testid="wallet-history">
        <h2 className="text-sm font-extrabold text-slate-900">Transaction history</h2>
        {!loading && transactions.length === 0 && (
          <p className="mt-3 text-sm text-slate-500" data-testid="wallet-history-empty">
            No transactions yet. Add money to get started.
          </p>
        )}
        <div className="mt-3 space-y-2">
          {transactions.map((txn) => {
            const credit = txn.type === "credit";
            return (
              <div
                key={txn.id}
                className="flex items-center gap-3 border-b border-slate-100 pb-2 last:border-0"
                data-testid={`wallet-txn-${txn.id}`}
              >
                <span
                  className={`grid h-9 w-9 shrink-0 place-items-center rounded-full ${
                    credit ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"
                  }`}
                >
                  {credit ? <ArrowDownLeft size={16} /> : <ArrowUpRight size={16} />}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-900">{txn.description}</p>
                  <p className="text-[11px] text-slate-400">
                    {new Date(txn.at).toLocaleString()} · {txn.txn_id}
                  </p>
                </div>
                <p className={`text-sm font-extrabold ${credit ? "text-emerald-600" : "text-red-600"}`}>
                  {credit ? "+" : "−"}
                  {formatINR(txn.amount)}
                </p>
              </div>
            );
          })}
        </div>
      </section>

    </main>
  );
}
